import { Button, Image, Label, TextField, Theme } from '@busybusy/webapp-react-ui';
import { EquipmentCategory, EquipmentCategoryCreateInput, EquipmentCategoryUpdateInput } from '__generated__/graphql';
import classNames from 'classnames';
import MediaEntryUploader from 'components/domain/media-entry/MediaEntryUploader/MediaEntryUploader';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import useEquipmentCategories from 'containers/manage-euipment/hooks/useEquipmentCategories';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import { useToastOpen } from 'contexts/ToastContext';
import { useOpenable } from 'hooks';
import { t } from 'i18next';
import { first, isNil, isNull } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { IMediaEntryUploaderValue } from 'types';
import { ClassName } from 'types/ClassName';
import { isValidString } from 'utils/stringUtils';
import EquipmentCatMakeModelDeleteProhibitedDialog from '../EquipmentCatMakeModelDeleteProhibitedDialog/EquipmentCatMakeModelDeleteProhibitedDialog';
import EquipmentIconLibraryDialog from '../EquipmentIconLibraryDialog/EquipmentIconLibraryDialog';
import './CreateEquipmentCategoryDialog.scss';

export interface ICreateEquipmentCategoryDialogProps {
  className?: ClassName;
  isOpen: boolean;
  selectedRow: EquipmentCategory | null;
  canDelete: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onDelete: () => void;
}

const CreateEquipmentCategoryDialog = (props: ICreateEquipmentCategoryDialogProps) => {
  const { className, isOpen, selectedRow, canDelete, onClose, onSubmit, onDelete } = props;

  const iconLibraryDialog = useOpenable();
  const deleteDialog = useOpenable();
  const deleteProhibitedDialog = useOpenable();
  const toast = useToastOpen();
  const { createEquipmentCategory, editEquipmentCategory } = useEquipmentCategories();

  const [name, setName] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [iconFile, setIconFile] = useState<File | null>(null);

  useEffect(() => {
    if (!isNull(selectedRow)) {
      setName(selectedRow.title ?? '');
      setIcon(selectedRow.imageUrl ?? null);
    } else {
      resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow]);

  const handleSubmit = async () => {
    if (!isValidString(name)) {
      toast({ label: t('Name is required'), theme: Theme.DANGER });
      return;
    }

    if (!isNull(selectedRow)) {
      const editInput: EquipmentCategoryUpdateInput = {
        id: selectedRow.id,
        title: name,
      };

      try {
        await editEquipmentCategory({ editInput, image: iconFile });
        resetAll();
        onSubmit();
      } catch (error) {
        toast({ label: t('Error creating equipment category'), theme: Theme.DANGER });
        // eslint-disable-next-line no-console
        console.error('Error:', error);
      }
    } else {
      const createInput: EquipmentCategoryCreateInput = {
        title: name,
        createdOn: DateTime.now().toISO(),
      };

      try {
        await createEquipmentCategory({ createInput, image: iconFile });
        resetAll();
        onSubmit();
      } catch (error) {
        toast({ label: t('Error creating equipment category'), theme: Theme.DANGER });
        // eslint-disable-next-line no-console
        console.error('Error:', error);
      }
    }
  };

  const handleClose = () => {
    onClose();
    resetAll();
  };

  const resetAll = () => {
    setName('');
    removeIcon();
  };

  const removeIcon = () => {
    setIcon(null);
    setIconFile(null);
  };

  const handleIconUpload = (data: IMediaEntryUploaderValue) => {
    const uploadedFile = first(data.newFiles);

    if (uploadedFile) {
      const objectUrl = URL.createObjectURL(uploadedFile);
      setIcon(objectUrl);
      setIconFile(uploadedFile);
    }
  };

  const handleIconSelection = (icon: File) => {
    const objectUrl = URL.createObjectURL(icon);
    setIcon(objectUrl);
    setIconFile(icon);
  };

  const handleDelete = () => {
    resetAll();
    onDelete();
    deleteDialog.close();
  };

  const handleDeleteClick = () => {
    if (canDelete) {
      deleteDialog.open();
    } else {
      deleteProhibitedDialog.open();
    }
  };

  const classes = classNames('create-equipment-category-dialog', className);

  return (
    <HeaderDialog
      className={classes}
      isOpen={isOpen}
      onClose={handleClose}
      title={t(`${!isNull(selectedRow) ? 'Edit' : 'Create'} Category`)}
      divider={false}
    >
      <div className="mx-8">
        <Label required={true}>{t('Name')}</Label>
        <TextField value={name} restrictTo={{ maxLength: 200 }} onChange={setName} />
        <HeaderText title={t('Icon')} className={'mt-8'}>
          {t('A placeholder image for any equipment belonging to this category that doesn’t have an uploaded image.')}
        </HeaderText>

        {isNil(icon) ? (
          <>
            <Button type={'secondary'} className={'mt-4'} onClick={iconLibraryDialog.open}>
              {t('Select From Library')}
            </Button>
            <div className="my-4">{'- or -'}</div>
            <MediaEntryUploader
              className={'my-4'}
              onChange={handleIconUpload}
              multiple={false}
              message={
                <p className="fw-semi-bold">
                  <span>{t('Click')}</span>&nbsp;{t('or drag to upload an icon')}
                </p>
              }
            />
          </>
        ) : (
          <div className="image-container my-2">
            <Image src={icon} />
            <div className="remove-link mx-5" onClick={removeIcon}>
              {t('Remove')}
            </div>
          </div>
        )}
        <Button className={'mt-2 mb-4'} type={'primary'} onClick={handleSubmit}>
          {!isNull(selectedRow) ? t('Update') : t('Create')}
        </Button>
        {!isNull(selectedRow) && (
          <div className={'delete-button mx-2'} onClick={handleDeleteClick}>
            {t('Delete')}
          </div>
        )}
      </div>
      <EquipmentIconLibraryDialog
        isOpen={iconLibraryDialog.isOpen}
        onClose={iconLibraryDialog.close}
        onSubmit={handleIconSelection}
      />
      <EquipmentCatMakeModelDeleteProhibitedDialog
        isOpen={deleteProhibitedDialog.isOpen}
        onClose={deleteProhibitedDialog.close}
        type={'category'}
      />
      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title={t('Delete category?')}
        onClose={deleteDialog.close}
        onDelete={handleDelete}
        showHeaderDivider={false}
      >
        {t('If you delete this item it will be gone forever. Are you sure you want to proceed?')}
      </DeleteConfirmationDialog>
    </HeaderDialog>
  );
};

export default CreateEquipmentCategoryDialog;
